/**
 * The session slice (D156): what the shell renders, and nothing the engine
 * holds.
 *
 * @remarks
 * Before D156 this slice held the canonical `RuntimeState` and the thunk ran
 * the engine on it. Now it holds the session VIEW the host hands back —
 * the same shape whether the host ran the turn in this browser or asked
 * `services/play-api` to — plus the game's description and the seed. The
 * world lives in the host (local mode) or on the edge (server mode); the
 * store is the shell's memory of what it was last shown.
 *
 * @packageDocumentation
 */

import type { GameDescription, PortReport, SessionView } from '@orpheus/api-contracts';

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

/** A session as opened: the game, its seed, and the first view. */
export interface SessionOpenedPayload {
	readonly game: GameDescription;
	/** A newer version the session could not follow silently (D163) — carried on the open, cleared by the answer. */
	readonly pendingPort?: PortReport;
	readonly seed: number;
	readonly view: SessionView;
}

export interface SessionSliceState {
	/** The game around the session — present exactly when `sessionStatus` is `ready`. */
	readonly game: GameDescription | undefined;
	/** The port the shell is asking about (D163), or nothing. */
	readonly pendingPort: PortReport | undefined;
	readonly seed: number | undefined;
	readonly sessionStatus: 'idle' | 'ready';
	/** The view last handed back by the host — present exactly when `sessionStatus` is `ready`. */
	readonly view: SessionView | undefined;
}

const initialState: SessionSliceState = {
	game: undefined,
	pendingPort: undefined,
	seed: undefined,
	sessionStatus: 'idle',
	view: undefined,
};

export const sessionSlice = createSlice({
	initialState,
	name: 'session',
	reducers: {
		/** Begin (or begin again): the host opened a session. */
		sessionOpened: (_sliceState, action: PayloadAction<SessionOpenedPayload>) => ({
			game: action.payload.game,
			pendingPort: action.payload.pendingPort,
			seed: action.payload.seed,
			sessionStatus: 'ready' as const,
			view: action.payload.view,
		}),
		/** Adopt the view after a turn — every verdict carries one (D23, projected). */
		viewAdopted: (sliceState, action: PayloadAction<SessionView>) => ({
			...sliceState,
			view: action.payload,
		}),
	},
});

export const { sessionOpened, viewAdopted } = sessionSlice.actions;
